import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Plus, Play, Pause, Pencil, Trash2, Zap, Bell } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useRecurringSubscriptions, useUpdateSubscription, useDeleteSubscription, useGenerateRecurringExpenses } from '@/hooks/finance/useRecurringSubscriptions';
import { RecurringSubscription } from '@/types/database/recurring-subscriptions';
import NewSubscriptionDialog from '@/components/finance/NewSubscriptionDialog';
import { SubscriptionBotConnectDialog } from '@/components/finance/SubscriptionBotConnectDialog';
import { format, isPast, isToday } from 'date-fns';

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { data: subscriptions, isLoading } = useRecurringSubscriptions();
  const updateMutation = useUpdateSubscription();
  const deleteMutation = useDeleteSubscription();
  const generateMutation = useGenerateRecurringExpenses();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<RecurringSubscription | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [botDialogOpen, setBotDialogOpen] = useState(false);

  const handleEdit = (sub: RecurringSubscription) => {
    setEditingSub(sub);
    setDialogOpen(true);
  };

  const handleToggleActive = (sub: RecurringSubscription) => {
    updateMutation.mutate({ id: sub.id, is_active: !sub.is_active });
  };

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId(null);
    }
  };

  const getDueStatus = (dateStr: string, isActive: boolean) => {
    if (!isActive) return <Badge variant="secondary">Paused</Badge>;
    const date = new Date(dateStr);
    if (isToday(date)) return <Badge variant="secondary" className="border-orange-300 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">Due Today</Badge>;
    if (isPast(date)) return <Badge variant="destructive">Overdue</Badge>;
    return <Badge variant="outline">Upcoming</Badge>;
  };

  const monthlyByCurrency = subscriptions
    ?.filter(s => s.is_active)
    .reduce((acc, s) => {
      const multiplier = { weekly: 4.33, monthly: 1, quarterly: 1 / 3, yearly: 1 / 12 }[s.frequency];
      const currency = s.currency || 'ETB';
      acc[currency] = (acc[currency] || 0) + s.amount * multiplier;
      return acc;
    }, {} as Record<string, number>) || {};

  return (
    <Layout>
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/finance/accounting/expenses')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Recurring Subscriptions</h1>
            <p className="text-muted-foreground">Manage subscriptions that auto-generate expenses</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={() => setBotDialogOpen(true)} title="Telegram Notifications">
            <Bell className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => generateMutation.mutate()} disabled={generateMutation.isPending}>
            <Zap className="h-4 w-4 mr-2" />
            {generateMutation.isPending ? 'Generating...' : 'Generate Now'}
          </Button>
          <Button onClick={() => { setEditingSub(null); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Subscription
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Active</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{subscriptions?.filter(s => s.is_active).length || 0}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Est. Monthly Cost</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(monthlyByCurrency).length === 0 ? (
              <div className="text-2xl font-bold">0.00 ETB</div>
            ) : (
              <div className="space-y-1">
                {Object.entries(monthlyByCurrency).map(([currency, total]) => (
                  <div key={currency} className="text-2xl font-bold">
                    {total.toLocaleString('en', { minimumFractionDigits: 2 })} {currency}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Overdue</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-destructive">{subscriptions?.filter(s => s.is_active && isPast(new Date(s.next_due_date)) && !isToday(new Date(s.next_due_date))).length || 0}</div></CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : !subscriptions?.length ? (
            <div className="p-8 text-center text-muted-foreground">
              No subscriptions yet. Click "Add Subscription" to create one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Next Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscriptions.map(sub => (
                  <TableRow key={sub.id} className={!sub.is_active ? 'opacity-50' : ''}>
                    <TableCell className="font-medium">{sub.name}</TableCell>
                    <TableCell>{sub.vendor_name || '-'}</TableCell>
                    <TableCell>{Number(sub.amount).toLocaleString('en', { minimumFractionDigits: 2 })} {sub.currency}</TableCell>
                    <TableCell className="capitalize">{sub.frequency}</TableCell>
                    <TableCell>{format(new Date(sub.next_due_date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>{getDueStatus(sub.next_due_date, sub.is_active)}</TableCell>
                    <TableCell>{sub.category?.name || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleToggleActive(sub)} title={sub.is_active ? 'Pause' : 'Resume'}>
                          {sub.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(sub)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {userRole === 'admin' && (
                          <Button variant="ghost" size="icon" onClick={() => setDeleteId(sub.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewSubscriptionDialog open={dialogOpen} onOpenChange={setDialogOpen} subscription={editingSub} />
      <SubscriptionBotConnectDialog open={botDialogOpen} onOpenChange={setBotDialogOpen} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subscription</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this subscription. Previously generated expenses will not be affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </Layout>
  );
}
